import React, { useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { BeheerLayout } from '../../components/beheer/BeheerLayout';
import { MemoryRow } from '../../components/beheer/geheugen/MemoryRow';
import { ActorMark } from '../../components/beheer/ActorMark';
import { ProcessingTag } from '../../components/ui/ProcessingTag';
import { useMemoryStore } from '../../hooks/useMemoryStore';
import { getAgent } from '../../utils/agents';
type Tab = 'voorstellen' | 'kennis' | 'werkwijze' | 'gebeurtenissen';
const TABS: {
  id: Tab;
  label: string;
  intro: string;
}[] = [{
  id: 'voorstellen',
  label: 'Voorstellen',
  intro: 'Wat Anna denkt te hebben geleerd. Niets wordt onthouden zonder jouw ok.'
}, {
  id: 'kennis',
  label: 'Kennis',
  intro: 'Feiten over mensen, plekken, je huis en je voorkeuren.'
}, {
  id: 'werkwijze',
  label: 'Werkwijze',
  intro: 'Hoe jij dingen gedaan wilt hebben. Anna volgt dit bij elke taak.'
}, {
  id: 'gebeurtenissen',
  label: 'Gebeurtenissen',
  intro: 'Wat er gebeurde, met een bewaartermijn. Daarna verdwijnt het vanzelf.'
}];
export function Geheugen() {
  const store = useMemoryStore();
  const [tab, setTab] = useState<Tab>('voorstellen');
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const match = (s: string) => !q || s.toLowerCase().includes(q);
  const counts: Record<Tab, number> = {
    voorstellen: store.proposals.length,
    kennis: store.knowledge.length,
    werkwijze: store.workflows.length,
    gebeurtenissen: store.events.length
  };
  const current = TABS.find(t => t.id === tab) ?? TABS[0];
  const btn = 'inline-flex h-8 items-center whitespace-nowrap rounded-full px-3 text-[12px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60';
  const rows = (() => {
    if (tab === 'voorstellen') return store.proposals.filter(p => match(p.text)).map(p => <MemoryRow key={p.id} label={p.kind === 'kennis' ? 'Kennis' : 'Werkwijze'} title={p.text} meta={<>
                <ActorMark actor={p.agent} size="sm" data-id="element-1835" data-mp="1j4pqqmlf2wrs" />
                <span data-id="element-1836" data-mp="1ecxze9ufsn0l">{getAgent(p.agent).name}</span>
                <span aria-hidden data-id="element-1837" data-mp="1ebmxkfkjxpc4">·</span>
                <span data-id="element-1838" data-mp="1ac48r8pm0u40">{p.evidence}</span>
                <span aria-hidden data-id="element-1839" data-mp="1ab2w4zsy3zk6">·</span>
                <span className="tabular-nums" data-id="element-1840" data-mp="15crp0wu1c35n">zekerheid {Math.round(p.confidence * 100)}%</span>
              </>} actions={<>
                <button type="button" onClick={() => store.reject(p.id)} className={`${btn} text-ink-muted hover:bg-white/[0.06] hover:text-ink`} data-id="element-1841" data-mp="10cdffacscl5i">
                  Niet onthouden
                </button>
                <button type="button" onClick={() => store.accept(p.id)} className={`${btn} bg-ink text-night-950 hover:bg-white`} data-id="element-1842" data-mp="retbturgacrq">
                  Onthoud
                </button>
              </>} data-id="element-1834" data-mp="3ihelvvs26vyn" />);
    if (tab === 'kennis') return store.knowledge.filter(k => match(k.text)).map(k => <MemoryRow key={k.id} label={k.topic} title={k.text} meta={<>
                <span data-id="element-1844" data-mp="h9lawki02gbw">{k.source}</span>
                <span aria-hidden data-id="element-1845" data-mp="lanch7ze34h1">·</span>
                <span data-id="element-1846" data-mp="l9de0homju9v">geleerd {k.learned}</span>
                <span aria-hidden data-id="element-1847" data-mp="3kps580eiz180">·</span>
                <span className="tabular-nums" data-id="element-1848" data-mp="3kqtup9wafq99">{k.uses}× gebruikt</span>
                <ProcessingTag where="lokaal" data-id="element-1849" data-mp="3polhxqlzmo74" />
              </>} onForget={() => store.forgetKnowledge(k.id)} data-id="element-1843" data-mp="chtie4faa0j4" />);
    if (tab === 'werkwijze') return store.workflows.filter(w => match(w.rule)).map(w => <MemoryRow key={w.id} label={w.area} title={w.rule} meta={<>
                {w.example && <span className="italic text-ink-soft" data-id="element-1851" data-mp="1dyax2n0ntv3o">{w.example}</span>}
                {w.example && <span aria-hidden data-id="element-1852" data-mp="1chdl68787pj0">·</span>}
                <span data-id="element-1853" data-mp="1cif4t12b5kcc">{w.learned}</span>
              </>} onForget={() => store.forgetWorkflow(w.id)} data-id="element-1850" data-mp="2hsyag9lbgdv" />);
    return store.events.filter(e => match(e.text)).map(e => <MemoryRow key={e.id} label={e.date} title={e.text} meta={<>
              <ActorMark actor={e.agent} size="sm" data-id="element-1855" data-mp="1vg9dtf3frvuq" />
              <span data-id="element-1856" data-mp="1vey8vby98i21">bewaard {e.keepUntil}</span>
              <ProcessingTag where={e.where} data-id="element-1857" data-mp="1qipy0ar3bb0o" />
            </>} onForget={() => store.forgetEvent(e.id)} data-id="element-1854" data-mp="1hh9py9nqzkzm" />);
  })();
  return <BeheerLayout title="Geheugen" intro="Alles wat Anna over je weet staat hier, lokaal op je eigen server. Je kunt alles inzien en vergeten." aside={<p className="text-[14px] text-ink-soft" data-id="element-1859" data-mp="2e823p0n7l0y8">
          {counts.voorstellen > 0 ? <span className="text-ink" data-id="element-1860" data-mp="299h3v2bvcjab">{counts.voorstellen} voorstellen wachten</span> : 'Geen open voorstellen'}
        </p>} data-id="element-1858" data-mp="258niaflwfgkl">
      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-b border-white/[0.06]" data-id="element-1861" data-mp="2ss4x77a7x6oi">
        <div role="tablist" aria-label="Geheugen" className="no-scrollbar -mb-px flex gap-1 overflow-x-auto" data-id="element-1862" data-mp="2xqycn592f7rb">
          {TABS.map(t => {
          const active = t.id === tab;
          return <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={active} aria-controls="memory-panel" onClick={() => setTab(t.id)} className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 pb-3 pt-1 text-[15px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${active ? 'border-accent text-ink' : 'border-transparent text-ink-muted hover:text-ink'}`} data-id="element-1863" data-mp="2nwza4552xlyu">
                {t.label}
                <span className="text-[12px] tabular-nums text-ink-muted" data-id="element-1864" data-mp="1pbejqkis4c7">{counts[t.id]}</span>
              </button>;
        })}
        </div>
        <label className="relative mb-3 flex items-center" data-id="element-1865" data-mp="3o2tf5b13gix9">
          <SearchIcon className="pointer-events-none absolute left-3 h-4 w-4 text-ink-muted" aria-hidden data-id="element-1866" data-mp="3o18kzmioz1zx" />
          <span className="sr-only" data-id="element-1867" data-mp="3jzwr34ssbulx">Zoek in geheugen</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Zoek in geheugen" className="h-9 w-64 rounded-full border border-white/10 bg-white/[0.03] pl-9 pr-3 text-[13px] text-ink placeholder:text-ink-muted focus:border-accent/50 focus:outline-none" data-id="element-1868" data-mp="klb835t5bfxw" />
        </label>
      </div>

      <section id="memory-panel" role="tabpanel" aria-labelledby={`tab-${tab}`} className="mt-6 max-w-5xl" data-id="element-1869" data-mp="2wcokmjjmefib">
        <p className="text-[14px] text-ink-muted" data-id="element-1870" data-mp="2rcssbhkaccil">{current.intro}</p>
        {rows.length > 0 ? <ul className="mt-4 divide-y divide-white/[0.06] border-y border-white/[0.06]" data-id="element-1871" data-mp="16vt0gpb1u1r4">{rows}</ul> : <p className="mt-6 rounded-2xl border border-dashed border-white/10 px-6 py-12 text-center text-[14px] text-ink-muted" data-id="element-1872" data-mp="1bv5y853gkt8h">
            {q ? 'Niets gevonden.' : tab === 'voorstellen' ? 'Alles is beoordeeld. Nieuwe voorstellen verschijnen hier.' : 'Leeg.'}
          </p>}
      </section>
    </BeheerLayout>;
}